import { fromEvent, Observable } from 'rxjs';
import { share, tap } from 'rxjs/operators';
import { isBlockLevelContent } from '../../helpers/dom/content';
import { unwrapElement } from '../../helpers/dom/element';
import {
  findAncestor,
  findAncestorSiblings,
  findNextNonWhitespaceSibling,
  findPreviousNonWhitespaceSibling,
} from '../../helpers/dom/node';
import { isParagraphElement } from '../../helpers/dom/paragraph';
import { createPointAtEndOfNode, createPointWithStartOfRange } from '../../helpers/dom/point';
import { ignoreDuringCompositions } from '../../helpers/ignoreDuringCompositions';
import { findInIterable, isEmptyIterable } from '../../helpers/iterable';
import { isBlockquoteElement, isQuoteElement } from '../../helpers/quote';
import { createPointsWithSelection } from '../../helpers/selection';
import { createConnectingService } from '../base/connectingService';
import { ElementSelection, ElementSelectionService } from '../dom/elementSelectionService';
import { ElementService } from '../dom/elementService';
import { NULL_QUOTE_SERVICE, QuoteService } from '../text/quoteService';

export function createHtmlBlockquoteService(
  element$: Observable<Element | null>,
  selectionService: ElementSelectionService,
): QuoteService {
  return createConnectingService(
    element$,
    () => NULL_QUOTE_SERVICE,
    element => new HtmlBlockquoteService(element, selectionService),
  );
}

interface HtmlBlockquoteState extends ElementSelection {
  blockquote: HTMLQuoteElement;
}

export class HtmlBlockquoteService extends ElementService<Element> implements QuoteService {
  constructor(element: Element, selectionService: ElementSelectionService) {
    super(
      element,
      {
        effects$: fromEvent<KeyboardEvent>(element, 'keydown').pipe(
          ignoreDuringCompositions(element),
          tap(event => {
            if (event.defaultPrevented) {
              return;
            }

            const { key } = event;
            switch (key) {
              case 'Tab':
                if (this.getCanChangeIndentLevel(key)) {
                  event.preventDefault();

                  if (event.shiftKey) {
                    this.outdent();
                  } else {
                    this.indent();
                  }
                }
                break;

              case 'Backspace':
                if (this.getCanChangeIndentLevel(key)) {
                  event.preventDefault();
                  this.outdent();
                }
                break;

              case 'Enter':
                if (this.maybeUnwrapLastParagraph()) {
                  event.preventDefault();
                }
                break;

              default:
            }
          }),
          share(),
        ),
      },
      // TODO: Make changes$ more fine-grained.
      selectionService,
    );

    this.selectionService = selectionService;
  }

  private readonly selectionService: ElementSelectionService;

  private get state(): HtmlBlockquoteState | undefined {
    const { selection } = this.selectionService;
    if (!selection) {
      return undefined;
    }

    const { range } = selection;
    const quote = findAncestor(range.commonAncestorContainer, isQuoteElement, this.element);
    if (!quote || !isBlockquoteElement(quote)) {
      return undefined;
    }

    return {
      ...selection,
      blockquote: quote,
    };
  }

  get isQuoted() {
    return !!this.state;
  }

  get canIndent() {
    return this.getCanChangeIndentLevel();
  }

  get canOutdent() {
    return this.getCanChangeIndentLevel();
  }

  private getCanChangeIndentLevel(key?: string) {
    // Check we have a blockquote.
    const { state } = this;
    if (!state) {
      return false;
    }

    const { range } = state;

    // Handle backspace only if we are at a collapsed range.
    if (key === 'Backspace' && !range.collapsed) {
      return false;
    }

    // If the selection is collapsed, check we're at the start of the blockquote.
    if (key && range.collapsed) {
      return createPointWithStartOfRange(range).isAtStartOfNode(
        state.blockquote,
        node => !findPreviousNonWhitespaceSibling(node),
        this.element,
      );
    }

    return true;
  }

  quote() {
    if (!this.isQuoted) {
      this.wrap();
    }
  }

  unquote() {
    this.unwrap(true);
  }

  indent() {
    if (this.canIndent) {
      this.wrap();
    }
  }

  outdent() {
    if (this.canOutdent) {
      this.unwrap();
    }
  }

  private wrap() {
    const { selection, extraSelectedElements } = this.selectionService;
    if (!selection) {
      return;
    }

    // We don't want to enable quoting of non-contiguous elements.
    if (!isEmptyIterable(extraSelectedElements)) {
      return;
    }

    // TODO: Use selectionService.withMutations.
    const points = createPointsWithSelection(selection);

    // Find the non-phrasing content ancestor siblings for the start and end of the range. Typically, these
    // are the starting and ending paragraph.
    const { range } = selection;
    const ancestorSiblings = findAncestorSiblings(range, isBlockLevelContent, this.element);
    if (!ancestorSiblings) {
      return;
    }

    const { startAncestor, endAncestor } = ancestorSiblings;
    const { parentNode, nextSibling } = endAncestor;
    if (!parentNode) {
      return;
    }

    // Wrap the siblings from the start to the end in a blockquote.
    const blockquote = this.document.createElement('blockquote');
    let childNode: Node | null = startAncestor;
    while (childNode && childNode !== nextSibling) {
      const nextChildNode: Node | null = childNode.nextSibling;
      blockquote.appendChild(childNode);
      childNode = nextChildNode;
    }

    // Insert the blockquote.
    parentNode.insertBefore(blockquote, nextSibling);

    // Restore the selection.
    this.selectionService.selectWithPoints(points);
  }

  private unwrap(shouldUnwrapRecursively = false) {
    const { selection } = this.selectionService;
    if (!selection) {
      return;
    }

    const { range } = selection;
    const node = range.commonAncestorContainer;
    // TODO: Use selectionService.withMutations.
    const points = createPointsWithSelection(selection);

    let didUnwrap = false;
    let prevQuote: HTMLQuoteElement | null = null;
    let quote: HTMLQuoteElement | null;
    while ((quote = findAncestor(node, isQuoteElement, this.element))) {
      // Prevent an infinite loop just in case the unwrapping fails.
      if (quote === prevQuote) {
        break;
      }

      unwrapElement(quote);
      prevQuote = quote;
      didUnwrap = true;

      if (!shouldUnwrapRecursively) {
        break;
      }
    }

    // Restore the selection, if necessary.
    if (didUnwrap) {
      this.selectionService.selectWithPoints(points);
    }
  }

  private maybeUnwrapLastParagraph(): boolean {
    const { state } = this;
    if (!state) {
      return false;
    }

    // Find the paragraph we're in, which must be a direct child of the blockquote.
    const { range, blockquote } = state;
    const paragraph = findInIterable(
      blockquote.childNodes,
      childNode => childNode.contains(range.commonAncestorContainer),
      null,
    );
    if (!isParagraphElement(paragraph)) {
      return false;
    }

    // Check this is the last paragraph.
    if (findNextNonWhitespaceSibling(paragraph)) {
      return false;
    }

    // Check we're at the start of it.
    const isAtStartOfParagraph = createPointWithStartOfRange(range).isAtStartOfNode(
      paragraph,
      node => !findPreviousNonWhitespaceSibling(node),
      blockquote,
    );
    if (!isAtStartOfParagraph) {
      return false;
    }

    createPointAtEndOfNode(blockquote).insertNode(paragraph);

    // Restore the selection.
    this.selectionService.selectWithOffsetsInContainers(state);

    return true;
  }
}
